import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Asterisk } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { Marquee, ComparisonTable, Checklist, Exhibit } from "../components/Blocks";
import { FAQ_LIST, SITE_DOMAIN, LAST_REVIEWED } from "../content/site";

const MARQUEE_ITEMS = [
  "Independent and anonymous",
  "No referral links",
  "Official sources only",
  "The good and the unproven",
  "Not the official site",
  "Skeptical going in",
];

const MONEY_CARDS = [
  { to: "/compensation-plan", n: "01", title: "The compensation plan, reviewed", body: "Four bonuses across twenty ranks, in the organization's own numbers." },
  { to: "/how-ranks-work", n: "02", title: "How ranks work", body: "Cumulative QV, the 50% rule, two legs: the mechanic I liked most, and the guardrails." },
  { to: "/points-and-ownership", n: "03", title: "Points & ownership", body: "Ownership-like participation units. No dividends. The biggest open item in my review." },
  { to: "/is-this-an-mlm", n: "04", title: "Is this an MLM?", body: "The question everyone asks. My honest, unhurried answer." },
  { to: "/is-this-a-security", n: "05", title: "Is this a security?", body: "The company line, and why you need your own counsel." },
];

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const ornamentY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const ornamentR = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "U.CENTER Explained",
        url: SITE_DOMAIN,
        description: "An independent, anonymously written review of U.CENTER and U-TOPIA. Not the official website of either organization.",
      },
      {
        "@type": "WebSite",
        name: "U.CENTER Explained",
        url: SITE_DOMAIN,
      },
    ],
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="U.CENTER and U-TOPIA, Reviewed: an independent deep-dive"
        description="I spent weeks researching U.CENTER (a registered DAO Association in the UAE) and U-TOPIA (a US fintech super app). Anonymous, unpaid, no referral links. Here's what I found."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: "92vh" }}>
        <motion.div
          aria-hidden="true"
          style={{ y: ornamentY, rotate: ornamentR, WebkitTextStroke: "1.5px rgba(47,93,80,0.28)", right: "-4vw", top: "6vh" }}
          className="absolute font-serif-display select-none pointer-events-none text-[38vw] leading-none text-transparent hidden lg:block"
        >
          <Asterisk className="spin-slow" style={{ width: "34vw", height: "34vw", color: "rgba(47,93,80,0.16)" }} strokeWidth={0.6} />
        </motion.div>

        <motion.div style={{ y: heroTextY }} className="relative max-w-6xl mx-auto px-5 pt-20 pb-16 md:pt-28">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-xs uppercase tracking-[0.3em] font-medium mb-10 flex items-center gap-3"
            style={{ color: "var(--accent)" }}
            data-testid="hero-eyebrow"
          >
            <span className="inline-block w-10 h-px" style={{ background: "var(--accent)" }} />
            An independent review · anonymous and unpaid
          </motion.p>

          <h1 className="font-serif-display font-light m-0" style={{ fontSize: "clamp(3rem, 9.5vw, 8.5rem)", lineHeight: 1.02, letterSpacing: "-0.02em", color: "var(--ink)" }} data-testid="hero-headline">
            <MaskedLine delay={0.15}>U.CENTER</MaskedLine>
            <MaskedLine delay={0.3}>
              <span>and <em style={{ color: "var(--accent)" }}>U-TOPIA</em>,</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>reviewed.</MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-xl"
          >
            <p className="text-lg leading-relaxed m-0" style={{ color: "var(--ink-muted)" }}>
              I spent weeks reading the documents, watching the official videos, and pressure-testing the claims, so you don't have to. I came in skeptical. I found more substance than I expected, and I kept the receipts. This is the honest version.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/what-is-ucenter" data-testid="hero-start-btn" className="outline-btn no-underline">
                Read the full review <ArrowRight size={16} />
              </Link>
              <Link to="/how-to-verify" data-testid="hero-diligence-btn" className="outline-btn outline-btn--accent no-underline">
                The 8 checks I ran
              </Link>
            </div>
            <p className="text-xs mt-8 m-0" style={{ color: "var(--ink-muted)" }}>No referral links · Last reviewed 7 September 2026 · 6 min read</p>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* CHAPTER 01 */}
      <section className="max-w-6xl mx-auto px-5 pt-24 md:pt-36">
        <Reveal>
          <div className="flex items-baseline gap-6 mb-4">
            <span className="chapter-numeral text-7xl md:text-8xl">01</span>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl m-0" style={{ color: "var(--ink)" }}>First, get the names right</h2>
          </div>
          <p className="max-w-2xl text-lg m-0" style={{ color: "var(--ink-muted)" }}>
            My biggest early confusion was which organization I was even looking at. There are two, they're legally separate, and the difference matters. Here is each, plainly.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-5 mt-12">
          {[
            {
              to: "/what-is-ucenter", tag: "The community layer", name: "U.CENTER",
              body: "A registered DAO Association in Ras Al Khaimah, UAE (Innovation City). Education, marketing, community, and ambassadors. Fully live since May 2026: platform, Academy, and platform voting all active.",
              tid: "card-ucenter",
            },
            {
              to: "/what-is-utopia", tag: "The product layer", name: "U-TOPIA",
              body: "A US-registered private company building a fintech super app: self-custody wallet, swaps, send/receive/withdraw, Visa tap-to-pay. The beta actually works; public launch 16 October at Flow Summit, Bangkok.",
              tid: "card-utopia",
            },
          ].map((c, i) => (
            <Reveal key={c.name} delay={i * 0.12}>
              <Link to={c.to} data-testid={c.tid} className="block no-underline card-lift h-full p-8 md:p-10 border relative overflow-hidden" style={{ background: "var(--card)", borderColor: "var(--line)", clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 28px), calc(100% - 28px) 100%, 0 100%)" }}>
                <p className="text-xs uppercase tracking-[0.25em] font-medium mb-6" style={{ color: "var(--accent)" }}>{c.tag}</p>
                <p className="font-serif-display font-light text-4xl md:text-5xl mb-5" style={{ color: "var(--ink)" }}>{c.name}</p>
                <p className="text-base leading-relaxed m-0" style={{ color: "var(--ink-muted)" }}>{c.body}</p>
                <p className="text-sm mt-6 mb-0 flex items-center gap-2 font-medium" style={{ color: "var(--accent)" }}>Read my take <ArrowRight size={14} /></p>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14">
            <ComparisonTable />
            <p className="text-sm m-0" style={{ color: "var(--ink-muted)" }}>
              The full relationship, including value flow, voting rights, and the hard line on returns, is at <Link to="/ucenter-vs-utopia" style={{ color: "var(--accent)" }}>U.CENTER vs U-TOPIA</Link>.
            </p>
          </div>
        </Reveal>
      </section>

      {/* EXHIBIT INTERLUDE */}
      <section className="max-w-6xl mx-auto px-5 pt-24 md:pt-36">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] font-medium mb-4" style={{ color: "var(--accent)" }}>Straight from the source</p>
          <h2 className="font-serif-display font-light text-4xl md:text-5xl m-0 max-w-3xl" style={{ color: "var(--ink)" }}>The official front door, as I found it</h2>
          <p className="max-w-2xl text-lg mt-5" style={{ color: "var(--ink-muted)" }}>
            Reviews should show their evidence. This is the live u.center homepage as it greeted me: “Where understanding comes first, and everything else follows.” More exhibits are embedded throughout the review, each captioned with its source and date.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <Exhibit
            src="/images/welcome.jpg"
            tid="exhibit-welcome"
            label="Exhibit · from u.center"
            alt="The official u.center homepage reading Welcome to U CENTER, the learning home of the U-TOPIA ecosystem, with open governance, DAO treasury, and education-first pillars."
            caption="u.center homepage, captured 7 September 2026. Note the three pillars along the bottom: Open Governance, DAO Treasury, Education First."
          />
        </Reveal>
      </section>

      {/* CHAPTER 02 */}
      <section className="max-w-6xl mx-auto px-5 pt-24 md:pt-36">
        <Reveal>
          <div className="flex items-baseline gap-6 mb-4">
            <span className="chapter-numeral text-7xl md:text-8xl">02</span>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl m-0" style={{ color: "var(--ink)" }}>What I'd check before paying</h2>
          </div>
          <p className="max-w-2xl text-lg m-0" style={{ color: "var(--ink-muted)" }}>
            These are the eight checks I ran before forming an opinion. Every one is doable today, from documents rather than chat messages. The full walkthrough is at <Link to="/how-to-verify" style={{ color: "var(--accent)" }}>how to verify</Link>.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <Checklist />
        </Reveal>
      </section>

      {/* CHAPTER 03 */}
      <section className="max-w-6xl mx-auto px-5 pt-24 md:pt-36">
        <Reveal>
          <div className="flex items-baseline gap-6 mb-4">
            <span className="chapter-numeral text-7xl md:text-8xl">03</span>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl m-0" style={{ color: "var(--ink)" }}>The money part, reviewed</h2>
          </div>
          <p className="max-w-2xl text-lg m-0" style={{ color: "var(--ink-muted)" }}>
            The compensation structure, in the organization's own numbers, with the honest caveats attached. A review that skips the risk section is an ad, not a review.
          </p>
        </Reveal>
        <div className="mt-12 border-t" style={{ borderColor: "var(--line)" }}>
          {MONEY_CARDS.map((c, i) => (
            <Reveal key={c.to} delay={i * 0.06}>
              <Link to={c.to} data-testid={`money-card-${i}`} className="group grid gap-2 md:grid-cols-[80px_1fr_auto] items-center py-7 border-b no-underline transition-colors" style={{ borderColor: "var(--line)" }}>
                <span className="chapter-numeral text-5xl">{c.n}</span>
                <span>
                  <span className="block font-serif-display text-2xl md:text-3xl font-light transition-transform duration-300 group-hover:translate-x-2" style={{ color: "var(--ink)" }}>{c.title}</span>
                  <span className="block text-sm mt-1" style={{ color: "var(--ink-muted)" }}>{c.body}</span>
                </span>
                <ArrowRight size={20} className="hidden md:block transition-transform duration-300 group-hover:translate-x-1" style={{ color: "var(--accent)" }} />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CHAPTER 04 */}
      <section className="max-w-6xl mx-auto px-5 pt-24 md:pt-36">
        <Reveal>
          <div className="flex items-baseline gap-6 mb-4">
            <span className="chapter-numeral text-7xl md:text-8xl">04</span>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl m-0" style={{ color: "var(--ink)" }}>The questions everyone asks</h2>
          </div>
          <p className="max-w-2xl text-lg m-0" style={{ color: "var(--ink-muted)" }}>
            One question, one page, one straight answer. Each citable in a single read.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-px mt-12 border" style={{ background: "var(--line)", borderColor: "var(--line)" }}>
          {FAQ_LIST.map((f, i) => (
            <Reveal key={f.slug} delay={(i % 2) * 0.08} className="h-full">
              <Link to={`/faq/${f.slug}`} data-testid={`faq-grid-${f.slug}`} className="flex items-center justify-between gap-4 h-full p-6 no-underline group transition-colors" style={{ background: "var(--card)" }}>
                <span className="font-serif-display text-xl md:text-2xl font-light" style={{ color: "var(--ink)" }}>{f.q}</span>
                <ArrowRight size={18} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1" style={{ color: "var(--accent)" }} />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OVERALL VERDICT */}
      <section className="max-w-6xl mx-auto px-5 pt-24 md:pt-36">
        <Reveal>
          <div data-testid="overall-verdict" className="grid md:grid-cols-[240px_1fr] gap-10 border p-8 md:p-14" style={{ background: "var(--card)", borderColor: "var(--accent)", clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 34px), calc(100% - 34px) 100%, 0 100%)" }}>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-medium mb-4" style={{ color: "var(--accent)" }}>My overall verdict</p>
              <p className="font-serif-display font-light leading-none m-0" style={{ fontSize: "clamp(4.5rem, 8vw, 7.5rem)", color: "var(--ink)" }}>8.5</p>
              <p className="text-sm mt-2 m-0" style={{ color: "var(--ink-muted)" }}>out of 10, on what's verifiable today</p>
            </div>
            <div className="space-y-5 text-base leading-relaxed" style={{ color: "var(--ink-muted)" }}>
              <p className="m-0">
                Here's where I land after weeks of research. This is <strong style={{ color: "var(--ink)" }}>early</strong>: points economics unpublished, on-chain governance unbuilt, an app still in beta, and a launch date that hasn't happened yet. Early means risk. There is a real chance you lose whatever you put in, so treat membership money as gone the moment you spend it.
              </p>
              <p className="m-0">
                But early is not the same as fake. The red flags I look for are absent: the principles are fully doxxed, the registration is published and QR-verifiable, the compensation plan matches its own one-pager, and the product exists in a working beta with a dated launch. Rug pulls don't usually arrive with named founders, physical offices, and five years of build history. On the evidence available, this is clearly not a scam, and the rug risk looks genuinely low.
              </p>
              <p className="m-0">
                U-TOPIA is also unusually well positioned: a self-custody super app with Visa tap-to-pay, opening to the public on a stated date. If they execute, this could credibly become the next Revolut-style story. U.CENTER's side is technically sound too: cumulative QV, real structural guardrails, a $25 entry point. If they keep shipping products, it could grow into a leading network-marketing DAO and a serious revenue engine for the whole ecosystem.
              </p>
              <p className="m-0 font-medium" style={{ color: "var(--ink)" }}>
                Score: 8.5 out of 10. What's holding it back from higher is simply what hasn't shipped yet: the white paper and the launch.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CLOSING */}
      <section className="max-w-6xl mx-auto px-5 pt-28 md:pt-40 pb-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Asterisk size={28} className="spin-slow mx-auto mb-8" style={{ color: "var(--accent)" }} />
            <p className="font-serif-display font-light text-4xl md:text-6xl leading-tight m-0" style={{ color: "var(--ink)" }}>
              I'm anonymous. <em style={{ color: "var(--accent)" }}>That's the point.</em>
            </p>
            <p className="text-lg mt-8 mb-10" style={{ color: "var(--ink-muted)" }}>
              This review stands on documents, not on who I am. I wasn't paid, I hold no membership, and there's no referral link anywhere on this site. Check my work: every claim traces to an official source.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/sources" data-testid="closing-sources-btn" className="outline-btn no-underline">See my sources</Link>
              <Link to="/contact" data-testid="closing-contact-btn" className="outline-btn outline-btn--accent no-underline">Correct my work</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
